// @ts-check
// ── The roster's filter predicates ────────────────────────────────────
//
// Lifted out of dashboard/app.js unchanged. The rule builder's UI stayed
// behind; what moved is the part that decides whether a client matches — the
// column list, the operators, the preset chips, and the two functions that read
// a client and answer a rule.
//
// ⚠️ THE DEPENDENCIES ARE INJECTED, NOT IMPORTED. rosterFieldValue reads seven
// derived values (Pulse level, lifetime spend, months as a client, days to
// renewal…) that still live in app.js and depend on module-level state there.
// Importing them would drag most of app.js into this file; injecting them keeps
// this one pure enough to unit test, which is the point of moving it.
//
// ⚠️ SYNTHETIC COLUMNS ARE MATCHED BY NAME AND HAVE NO ROSTER_FILTERS ENTRY.
// `__renew`, `__missedcheckin` and `__cancelrequested` are answered at the top
// of matchRosterRule and deliberately do not appear in the rule builder's
// column dropdown — they are preset chips, not hand-buildable rules.

import { continuityRateOf } from './continuity-rate.js';

/** @type {any} */
let deps = {};

/**
 * Hand this module the app.js functions it reads clients through.
 * Call once at boot, before the first render.
 *
 * @param {object} d
 * @returns {void}
 */
export function setRosterFilterDeps(d) { deps = d || {}; }

export const ROSTER_FILTERS = [
  { key: 'status',                label: 'Status',       ftype: 'select', options: ['Active', 'Pending Cancellation', 'Cancelled', 'Paused'] },
  { key: 'service',               label: 'Service',      ftype: 'select', options: ['Online', 'Hybrid', 'In Person'] },
  { key: 'billing',               label: 'Billing',      ftype: 'select', options: ['MRR', 'PIF', 'Installments', 'Comped'] },
  { key: 'mrrAmount',             label: 'MRR',          ftype: 'number' },
  { key: 'ltv',                   label: 'LTV',          ftype: 'number' },
  { key: 'moneyCollectedUpfront', label: 'Collected',    ftype: 'number' },
  { key: 'agreementValue',        label: 'Value',        ftype: 'number' },
  { key: 'continuityRate',        label: 'Continuity',   ftype: 'number' },
  { key: 'installmentCount',      label: 'Installments', ftype: 'number' },
  { key: 'agreementMonths',       label: 'Agreement',    ftype: 'number' },
  { key: 'months',                label: 'Months',       ftype: 'number' },
  { key: 'renewalDays',           label: 'Days to renewal', ftype: 'number' },
  { key: 'signUpDate',            label: 'Sign-up date', ftype: 'date' },
  { key: 'programStartDate',      label: 'Start date',   ftype: 'date' },
  { key: 'endDate',               label: 'End date',     ftype: 'date' },
  { key: 'checkinDay',            label: 'Check-in',     ftype: 'select', options: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] },
  { key: 'name',                  label: 'Client',       ftype: 'text' },
];
// A list saved with a Pulse rule before 10/20 still names it in the filter builder, instead of showing the first
// filter above with Pulse's old value. The rule matches nobody (matchRosterRule).
export const RETIRED_PULSE_FILTER = { key: 'pulse', label: 'Pulse (switched off)', ftype: 'select', options: [] };
export const ROSTER_OPS = {
  text:   ['contains', 'is', 'is not', 'starts with'],
  select: ['is', 'is not'],
  number: ['is', 'is not', 'is more than', 'is at least', 'is less than', 'is at most'],
  date:   ['is on', 'is before', 'is after'],
};
export const ROSTER_PRESETS = [
  { id: 'all',       name: 'All clients',  rules: [] },
  { id: 'highvalue', name: 'High value',   rules: [{ col: 'ltv',   op: 'is at least', val: '6000' }] },
  { id: 'renew30',   name: 'Renewals 30d', rules: [{ col: '__renew', op: 'is', val: 'yes' }] },
  // ⚠️ THE SAME RULE PULSE USES, NOT A NEW ONE. A weekly check-in is "missed"
  // once 8+ days have passed since the last one (or there has never been one),
  // for a client who HAS a check-in day and is past the 28-day settling-in
  // period — at-risk-reasons.js draws the line in the same place, so what this
  // list shows and what the Pulse warnings say can never disagree. Tick "select
  // all" on the result and the bulk message / bulk SMS buttons do the rest.
  // Away days come off the days counted (missedCheckin below, away dates part D).
  { id: 'missedcheckin', name: 'Missed check-in', rules: [{ col: '__missedcheckin', op: 'is', val: 'yes' }] },
  // ⚠️ NOT THE pending_cancellation STATUS. Ruling R73: that status is set by
  // the COACH, and an inbound cancellation request does not touch it. This chip
  // is derived from open cancellation_saves rows and changes nothing about any
  // client. Without it there is no queue at all — one notification fires and
  // then the row exists only inside that client's profile, so a missed
  // notification is a missed cancellation, permanently.
  { id: 'cancelrequested', name: 'Cancellation requested', rules: [{ col: '__cancelrequested', op: 'is', val: 'yes' }] },
];

// Normalise a date (string or Date) to a comparable 'YYYY-MM-DD'. String dates
// pass through by regex (no Date parse → no timezone shift); Date objects use
// local components to match the table's displayed day.
export function asDateStr(x) {
  if (!x) return '';
  if (!(x instanceof Date)) {
    const m = String(x).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    x = new Date(x);
  }
  if (isNaN(x)) return '';
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}

export function rosterFieldValue(c, key) {
  switch (key) {
    case 'status':                return deps.label(c.status);
    case 'service':                return deps.label(c.serviceType);
    case 'billing':               return deps.label(c.billingStructure);
    case 'mrrAmount':             return deps.getMonthlyRecurring(c);
    case 'ltv':                   return deps.getClientLifetimeSpend(c);
    case 'moneyCollectedUpfront': return Number(c.moneyCollectedUpfront) || 0;
    case 'agreementValue':        return Number(c.agreementValue) || 0;
    case 'continuityRate':        return continuityRateOf(c);
    case 'installmentCount':      return Number(c.installmentCount) || 0;
    case 'agreementMonths':       return (Number(c.lengthOfAgreement) || 0) + (Number(c.bonusMonths) || 0);
    case 'months':                return deps.getMonthsAsClient(c);
    case 'renewalDays':           { const d = deps.getDaysUntilEnd(c); return d == null ? '' : d; }
    case 'signUpDate':            return asDateStr(c.signUpDate);
    case 'programStartDate':      return asDateStr(c.programStartDate);
    case 'endDate':               return asDateStr(deps.getEndDate(c));
    case 'checkinDay':            return c.checkinDay ? c.checkinDay.charAt(0).toUpperCase() + c.checkinDay.slice(1) : '';
    case 'name':                  return c.name || '';
    default:                      return '';
  }
}

// Has this client missed their check-in?
//
// The rule, in words: they have a check-in day, they have been with you at
// least four weeks, and it has been eight or more days since their last
// check-in — or there has never been one. `lastCheckinAt` arrives with the
// Pulse map (churn-score-get ?all=true), so before that loads nobody matches
// rather than everybody.
//
// ⚠️ AWAY DAYS ARE LEFT OUT (away dates part D, Clayton 10/23: while a client
// is away "it wouldnt count against them in any regard"). `checkinAwayDays`
// rides on the same map: the client's away days since the day of their last
// check-in (or over the 30 days, when there is none), which come off the days
// counted. The 8 stays. A client 7 days past a check-in who then goes away is
// not listed while away; one already 10 days past before leaving still is. An
// answer without it (a failed away read) is judged as before.
//
// ⚠️ churnScores IS PASSED IN, NOT INJECTED, for the same reason as
// hasOpenCancellationRequest below: app.js hands over its live _churnScores
// map on every call through a one-line adapter of the same name.
export function missedCheckin(c, churnScores) {
  if (!c || c.status !== 'active' || !c.checkinDay) return false;
  const anchor = c.programStartDate || c.signUpDate;
  if (!anchor) return false;
  const daysSinceStart = Math.floor((Date.now() - new Date(anchor + 'T00:00:00').getTime()) / 86400000);
  if (daysSinceStart < 28) return false;
  const r = (churnScores || {})[c.id];
  if (!r || !('lastCheckinAt' in r)) return false;      // not loaded yet — say nothing
  // Never checked in: the whole 30-day window the map looks back over.
  const days = r.lastCheckinAt ? Math.floor((Date.now() - new Date(r.lastCheckinAt).getTime()) / 86400000) : 30;
  const due = days - (Number(r.checkinAwayDays) || 0);
  return due >= 8;
}

// Has this client asked to cancel, and is that request still open?
//
// ⚠️ NOT THEIR STATUS. Ruling R73: `pending_cancellation` is set by the coach
// when they action a cancellation, and an inbound request does not set it. This
// reads the open-request fact that rides along on the Pulse map (churn-score-get
// ?all=true), so the two can never be confused for one another.
//
// ⚠️ churnScores IS PASSED IN, NOT INJECTED. app.js's own _churnScores is
// module-level state this file can't see — app.js hands it over fresh on every
// call (see the one-line adapter of the same name there), the same way
// missedCheckin above is handed it.
//
// Before the map loads (or for a client with no entry yet) nobody matches,
// rather than everybody — same rule as missedCheckin.
export function hasOpenCancellationRequest(c, churnScores) {
  if (!c) return false;
  const r = (churnScores || {})[c.id];
  if (!r || !('cancelRequestedAt' in r)) return false;   // not loaded — say nothing
  return !!r.cancelRequestedAt;
}

export function matchRosterRule(c, rule) {
  if (rule.col === '__renew') { const d = deps.getDaysUntilEnd(c); return d !== null && d >= 0 && d <= 30; }
  if (rule.col === '__missedcheckin') return deps.missedCheckin(c);
  if (rule.col === '__cancelrequested') return deps.hasOpenCancellationRequest(c);
  // ⚠️ PULSE WAS SWITCHED OFF (Clayton 10/20; "Needs you this week" replaces At risk). A list he saved with a Pulse
  // rule before then matches nobody: dropped like any unknown column below, it would quietly show everyone.
  if (rule.col === 'pulse') return false;
  const col = ROSTER_FILTERS.find(f => f.key === rule.col);
  if (!col) return true;
  const v = rule.val;
  if (v === '' || v == null) return true;
  const fv = rosterFieldValue(c, rule.col);
  if (col.ftype === 'number') {
    if (fv === '' || fv == null) return false; // no value (e.g. no renewal date) can't satisfy a numeric compare
    const rv = Number(fv) || 0, cv = Number(v);
    if (isNaN(cv)) return true;
    switch (rule.op) {
      case 'is':           case '=': return rv === cv;
      case 'is not':       case '≠': return rv !== cv;
      case 'is more than': case '>': return rv > cv;
      case 'is at least':  case '≥': return rv >= cv;
      case 'is less than': case '<': return rv < cv;
      case 'is at most':   case '≤': return rv <= cv;
    }
    return true;
  }
  if (col.ftype === 'date') {
    const fvd = String(fv);
    if (!fvd) return false;                 // no date on file → excluded
    const vd = asDateStr(v);
    if (!vd) return true;                    // no date entered yet → don't filter
    switch (rule.op) { case 'is on': return fvd === vd; case 'is before': return fvd < vd; case 'is after': return fvd > vd; }
    return true;
  }
  const sv = String(fv);
  if (col.ftype === 'select') return rule.op === 'is' ? sv === v : sv !== v;
  const a = sv.toLowerCase(), b = String(v).toLowerCase();
  switch (rule.op) { case 'contains': return a.includes(b); case 'is': return a === b; case 'is not': return a !== b; case 'starts with': return a.startsWith(b); }
  return true;
}
